import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { countTotalRows } from "@/components/spatialAnalysis/catchmentDataSection/catchmentDataset";
import { RootState } from "@/store";
import { ECatchmentType, ERoadAccessMeasurement } from "@/types/spatial";

import { booleanValid } from "@turf/turf";

const useSpatialCatchment = () => {
  const [isDisabled, setIsDisabled] = useState(true); // Initialize as disabled

  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );

  const {
    radiusData = [],
    catchmentType,
    placeCoordinates,
    roadAccessConfig
  } = catchmentSettingsTemp;

  const handleToggleDisable = () => {
    const totalPOIs =
      countTotalRows(catchmentSettingsTemp?.datasetConfig?.datasets) ?? 0;
    const hasCoordinateData =
      (placeCoordinates && placeCoordinates?.length > 0) || totalPOIs > 0;

    const byRadiusValid =
      radiusData?.filter((item) => item.trim() !== "")?.length > 0;

    const byRoadAccessValid = roadAccessConfig?.routingType;

    const distanceValid =
      roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE &&
      roadAccessConfig?.distances &&
      roadAccessConfig?.distances?.filter((item) => item.trim() !== "")
        ?.length > 0;

    const durationValid =
      roadAccessConfig?.measurementType === ERoadAccessMeasurement.DURATION &&
      roadAccessConfig?.durations &&
      roadAccessConfig?.durations?.filter((item) => item.trim() !== "")
        ?.length > 0;

    const hasValidIsochroneCoordinates = () =>
      !!roadAccessConfig?.isochroneData?.length &&
      roadAccessConfig.isochroneData.every(
        (iso) =>
          Array.isArray(iso.data) &&
          iso.data.every((featureCollection) =>
            featureCollection.features.every(booleanValid)
          )
      );

    if (
      (!hasCoordinateData && distanceValid) ||
      (!hasCoordinateData && durationValid)
    ) {
      setIsDisabled(true);
      return;
    }
    let value = true;
    if (catchmentType === ECatchmentType.BY_RADIUS) {
      value = !byRadiusValid; // Return the *opposite* of byRadiusValid
    } else if (catchmentType === ECatchmentType.BY_ROAD) {
      const hasValidCoordinates = hasValidIsochroneCoordinates();
      value = !(
        byRoadAccessValid &&
        (distanceValid || durationValid) &&
        hasValidCoordinates
      ); // Return the *opposite*
    }
    setIsDisabled(value);
  };

  useEffect(() => {
    handleToggleDisable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catchmentSettingsTemp]); // Re-run when catchmentSettings changes

  return { isDisabled };
};

export default useSpatialCatchment;
